import React from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Tooltip,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaGitAlt, FaCalendarAlt, FaClock } from 'react-icons/fa';

interface VersionInfoProps {
  variant?: 'full' | 'compact' | 'minimal';
  position?: 'fixed' | 'static';
}

const VersionInfo: React.FC<VersionInfoProps> = ({ 
  variant = 'compact', 
  position = 'static' 
}) => {
  // 从环境变量或构建时注入的全局变量获取版本信息
  const commitId = process.env.NEXT_PUBLIC_COMMIT_ID || 'unknown';
  const buildDate = process.env.NEXT_PUBLIC_BUILD_DATE || new Date().toISOString();
  const buildTime = process.env.NEXT_PUBLIC_BUILD_TIME || new Date().toLocaleTimeString();
  
  // 格式化日期
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // 格式化时间
  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return buildTime;
    }
  };

  const bgColor = useColorModeValue('gray.100', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // 短版本的 commit ID (前7位)
  const shortCommitId = commitId.substring(0, 7);

  if (variant === 'minimal') {
    return (
      <Tooltip label={`构建时间: ${formatDate(buildDate)} ${formatTime(buildDate)}`}>
        <Badge
          colorScheme="gray"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
          cursor="help"
        >
          {shortCommitId}
        </Badge>
      </Tooltip>
    );
  }

  if (variant === 'compact') {
    return (
      <Box
        bg={bgColor}
        borderColor={borderColor}
        borderWidth="1px"
        borderRadius="md"
        px={3}
        py={2}
        fontSize="xs"
        color={textColor}
        position={position}
        bottom={position === 'fixed' ? 4 : undefined}
        right={position === 'fixed' ? 4 : undefined}
        zIndex={position === 'fixed' ? 1000 : undefined}
      >
        <HStack spacing={3}>
          <HStack spacing={1}>
            <Icon as={FaGitAlt} boxSize={3} />
            <Text fontFamily="mono">{shortCommitId}</Text>
          </HStack>
          <HStack spacing={1}>
            <Icon as={FaCalendarAlt} boxSize={3} />
            <Text>{formatDate(buildDate)}</Text>
          </HStack>
          <HStack spacing={1}>
            <Icon as={FaClock} boxSize={3} />
            <Text>{formatTime(buildDate)}</Text>
          </HStack>
        </HStack>
      </Box>
    );
  }

  // full variant
  return (
    <Box
      bg={bgColor}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="md"
      p={4}
      width="100%"
    >
      <VStack spacing={3} align="stretch">
        <Text fontSize="sm" fontWeight="bold" color={textColor}>
          版本信息
        </Text>
        
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Icon as={FaGitAlt} boxSize={4} color="orange.400" />
              <Text fontSize="sm" color={textColor}>
                Commit ID
              </Text>
            </HStack>
            <Badge
              colorScheme="orange"
              fontSize="xs"
              fontFamily="mono"
              px={2}
              py={1}
            >
              {shortCommitId}
            </Badge>
          </HStack>

          <HStack justify="space-between">
            <HStack spacing={2}>
              <Icon as={FaCalendarAlt} boxSize={4} color="blue.400" />
              <Text fontSize="sm" color={textColor}>
                构建日期
              </Text>
            </HStack>
            <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
              {formatDate(buildDate)}
            </Badge>
          </HStack>

          <HStack justify="space-between">
            <HStack spacing={2}>
              <Icon as={FaClock} boxSize={4} color="green.400" />
              <Text fontSize="sm" color={textColor}>
                构建时间
              </Text>
            </HStack>
            <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
              {formatTime(buildDate)}
            </Badge>
          </HStack>
        </VStack>

        {commitId !== 'unknown' && (
          <Tooltip label="点击复制完整 Commit ID">
            <Text
              fontSize="xs"
              color={textColor}
              fontFamily="mono"
              cursor="pointer"
              onClick={() => navigator.clipboard?.writeText(commitId)}
              _hover={{ color: 'blue.400' }}
            >
              完整 ID: {commitId}
            </Text>
          </Tooltip>
        )}
      </VStack>
    </Box>
  );
};

export default VersionInfo;
